import { INFORMATION_REFERENCE_SCHEMA } from "../information/information-extraction.js";
import { INFORMATION_DOCUMENT_FILE_LIMIT } from "../information/limit.js";
import { HELP_DOCUMENT_FILE_NAME } from "../help/manifest.js";
import { HELP_ENGINE_CAPABILITY } from "./catalogue.js";
/**
 * Fixed UUID v7 values, one per row.
 *
 * Fixed rather than generated because the catalogue is a platform table seeded by a migration
 * and read by the console; a regenerated id on a re-seed would orphan every
 * `voice_agent.agent_template_id` pointing at it.
 */
export const AGENT_TEMPLATE_UUID = {
    delivery: "019318a0-0000-7000-8000-000000000001",
    survey: "019318a0-0000-7000-8000-000000000002",
    booking: "019318a0-0000-7000-8000-000000000003",
    support: "019318a0-0000-7000-8000-000000000004",
    leads: "019318a0-0000-7000-8000-000000000005",
    receptionist: "019318a0-0000-7000-8000-000000000006",
    bi: "019318a0-0000-7000-8000-000000000007",
    /**
     * plan-019 TPL: the hidden `help` template (never offered to customers), published by migration 0156. `…0008`, not
     * `…0007` as first written: the BI Agent's `bi` row took `…0007` on `origin/main` first (INT, 2026-10-15).
     */
    help: "019318a0-0000-7000-8000-000000000008",
};
const id = (value) => value;
/** The hidden help template's code (plan-019 TPL, OD-20): the help assistant's VoiceAgent is built from it. */
export const HELP_AGENT_TEMPLATE_CODE = "help";
/** `[speaker, text]`, with the transcript speaker labels of the glossary (C1e). */
const line = (speaker, text) => ({
    speaker,
    text,
});
/**
 * DELIVERY — the field set the real pizzabot consumes, not the prototype's guess.
 *
 * The prototype asked for: an uploaded menu, an order-system API, an optional payments API
 * and a free-text opening-hours line. Four corrections, each traceable to a line of pizzabot:
 *
 * 1. **The menu upload is the whole catalogue.** `menu/dominos.md` is one markdown file that
 *    carries sizes, crusts, pizzas with per-size prices, per-size topping surcharges and any
 *    number of single-price categories — and it drives pricing, the tool schema, the system
 *    prompt **and** the speech-recognition bias at once. The prototype's blurb ("prices, sizes
 *    and modifiers") names three of those and misses the fourth, which is the one that decides
 *    whether the recogniser hears "κανονική" or "καλή νίκη" on an 8 kHz line.
 * 2. **Spoken names are per language and there is no fallback.** pizzabot carries an optional
 *    sibling overlay (`menu/dominos.es.json`, `{ "<id>": "<label>" }`) and, on a miss, reads
 *    the Greek name inside an English sentence. A second language therefore needs a second
 *    upload, and without it that language is unavailable.
 * 3. **The delivery fee and the free-delivery threshold are inputs, not menu content.**
 *    pizzabot reads them from `DELIVERY_FEE` and `FREE_DELIVERY_OVER` with `??` defaults. They
 *    are nowhere in the markdown, they change the total the caller is told, and there is no
 *    platform default — so they are required fields.
 * 4. **Orders have two possible destinations.** The prototype assumed a customer-owned order
 *    API. WeTalk ships the order book itself (lifted from `pizzabot/order-system/`, REST
 *    surface byte-identical), so the destination is a `book` field, and the customer's own
 *    system is the `api` alternative.
 */
/** The templates that ask: the wizard's "Opening hours" text field, read by the build's grammar. */
const ASKED_IN_OPENING_HOURS = { kind: "field", fieldCode: "opening_hours" };
/*
 * WHO EACH TEMPLATE TALKS TO, AND WHETHER IT PASSES (amended 2026-10-19, plan-021 DOM, ED-2, design §33.3.1, ADR 0073
 * proposed; OD-3 — the owner's default, accepted at AP-0). Every row states `audience` and `voiceAgentPass`, required on
 * `CataloguedAgentTemplate`, so a new row cannot forget them; neither is a column of `agent_template` (the seed renders
 * the table's columns only). The callers' templates pass and receive among themselves; the BI Agent is internal and
 * passes only to internal VoiceAgents (the audience wall, `mayPassBetween`); the survey neither passes nor receives in
 * this release (its interviewer has no tool loop, investigation IQ-6), and the hidden help template never does.
 */
const EXTERNAL_PASSING = { audience: "external", voiceAgentPass: { mayPass: true, mayReceive: true } };
const INTERNAL_PASSING = { audience: "internal", voiceAgentPass: { mayPass: true, mayReceive: true } };
const NEVER_PASSING = { audience: "external", voiceAgentPass: { mayPass: false, mayReceive: false } };
/*
 * WHETHER THE SECOND MODEL BACKUP MAY SERVE EACH TEMPLATE (added 2026-10-24, plan-027 §9.5, ADR 0079; the owner's decision
 * "All but support and help"). Every row states `secondModelBackupEligible`, required on `CataloguedAgentTemplate`:
 * delivery, survey, booking, leads, receptionist and bi are served by it when no GPU pool and the first backup can; the
 * `support` template (information answering — the second backup's grounding evaluation was not met) and the hidden help
 * template never are — their conversations then behave exactly as when no model is available.
 */
/*
 * WHAT CALLER CONFIRMATION MAY PROTECT ON EACH TEMPLATE (added 2026-10-29, plan-029 PC-DOM, design §40.8.1, ADR 0082
 * proposed; D-7, DD29-4, DD29-5). Every row states `protectedCapability`, required on `CataloguedAgentTemplate`: the BI
 * Agent its data queries (`bi_query`); delivery the known-caller recall (`caller_recall`, the gated
 * `recall_caller_details`) and the operations of the customer's own connections (`connection_operation` — its `api`
 * fields; never the template-provided `update_order` or the payment tools); every other row nothing — `support` has no
 * connections today, the survey and the hidden help template are never protected, and the unavailable rows have no `api`
 * field. A row declares `connection_operation` exactly when its field set has an `api` field (the suite pins it).
 */
/*
 * WHETHER EACH TEMPLATE ENDS ITS OWN CONVERSATIONS (added 2026-10-20, plan-022, design §34.3). Every row states
 * `mayEndConversation`, required on `CataloguedAgentTemplate`: the templates whose VoiceAgents converse through the model
 * are offered `end_conversation` (behind WETALK_RUNTIME_END_CONVERSATION); the survey keeps its interviewer's scripted
 * endings and the hidden help template its own host's, so neither is offered it.
 */
const DELIVERY_FIELD_SET = [
    {
        kind: "upload",
        code: "menu",
        label: "Menu file (required)",
        accept: [".md", ".csv", ".pdf", ".png", ".jpg", ".jpeg"],
    },
    {
        kind: "upload",
        code: "menu_label_overlay",
        label: "Item names in your other languages",
        accept: [".json", ".csv"],
    },
    { kind: "book", code: "order_destination", label: "Where confirmed orders go" },
    { kind: "api", code: "order_system", label: "Your own order system, instead of ours" },
    { kind: "api", code: "payment", label: "Payments" },
    { kind: "text", code: "delivery_fee", label: "Delivery fee", maxLength: 24 },
    {
        kind: "text",
        code: "free_delivery_over",
        label: "Free delivery over, or none",
        maxLength: 24,
    },
    { kind: "text", code: "opening_hours", label: "Opening hours", maxLength: 240 },
];
/**
 * SURVEY — the field set the real surveybot consumes, not the prototype's guess.
 *
 * The prototype asked for one upload: *"One question per line. Scale questions are read as
 * 'one to five'."* That is not what a survey agent runs on. `surveybot/server/script.ts` loads
 * **one JSON file per language** holding *every approved line the bot may say* — "the server
 * never improvises text: everything spoken comes from here" — and validates it at startup.
 *
 * The consequence the prototype could not see: **eight of those approved lines are factual
 * claims about the call itself**, and a question list cannot supply them. "Ποιος κάνει την
 * έρευνα;" has the answer "Είναι μια ανεξάρτητη έρευνα γνώμης. Ούτε ζητάμε ούτε καταγράφουμε
 * ονόματα." — a statement about who is calling, what is recorded and on whose behalf. WeTalk
 * must not invent any of it, so the three facts it needs are asked for as fields, and the
 * extraction drafts the script around them for the customer to review.
 *
 * The questionnaire upload therefore accepts the finished article (`.json`) as well as the
 * question list the prototype imagined.
 *
 * CORRECTED 2026-10-28 (plan-030 DOM-Q, design §41.7): this comment used to say a `.json` upload
 * "skips extraction entirely"; no code did that — every build ran the extraction model over every
 * upload. With the factory's questionnaire carry (plan-030 FAC-CARRY), a `.json` whose text is a
 * valid `wetalk-survey-json-v2` file is validated by `surveyQuestionnaireEditOf` and used verbatim,
 * with no extraction model call (Release 2 adds `-v3`); any other file — the question list, or a
 * `surveybot-survey-json-v1` file — is still read by the extraction model.
 */
const SURVEY_FIELD_SET = [
    {
        kind: "choice", code: "survey_arm_count", label: "How many survey arms?", defaultValue: "1",
        options: [{ value: "1", label: "One arm — everyone answers the same questions" }, { value: "2", label: "Two arms — a separate questionnaire for each group" }],
    },
    {
        kind: "upload",
        code: "questionnaire",
        label: "Your questions",
        accept: [".json", ".csv", ".md", ".txt"],
        required: true,
        allowText: true,
    },
    {
        kind: "upload", code: "questionnaire_arm2", label: "Questions for group 2",
        accept: [".json", ".csv", ".md", ".txt"],
        required: true,
        allowText: true,
        when: { field: "survey_arm_count", value: "2" },
    },
    { kind: "text", code: "interviewer_name", label: "The agent's name", maxLength: 60 },
    { kind: "text", code: "survey_subject", label: "What the survey is about", maxLength: 200 },
    {
        kind: "text",
        code: "survey_commissioner",
        label: "Who is calling, as people should hear it",
        maxLength: 240,
    },
];
const deliveryAgentTemplate = {
    ...EXTERNAL_PASSING,
    mayEndConversation: true,
    secondModelBackupEligible: true,
    protectedCapability: ["caller_recall", "connection_operation"],
    conversationEngine: { id: "model", supportedMedia: ["phone", "web_chat", "sandbox"] },
    agentTemplateId: id(AGENT_TEMPLATE_UUID.delivery),
    code: "delivery",
    direction: "inbound",
    customerOffered: true,
    name: "Delivery & ordering",
    dek: "Takes phone orders from your menu, checks the address and sends the order to your kitchen or till.",
    youAdd: "your menu",
    icon: "ph-duotone ph-moped",
    available: true,
    contentTitle: "Your menu and where orders go",
    contentDek: "The agent reads sizes, crusts, prices, extras and the words your customers use for each " +
        "item from one menu file. Confirmed orders go to the order book here, or to your own " +
        "system.",
    fieldSet: DELIVERY_FIELD_SET,
    defaultGreeting: "Roma Pizzeria, good evening. What can I get for you?",
    defaultName: "Roma orders",
    outcomeVocabulary: ["Order placed", "Asked a question only", "Transferred to staff", "Hung up"],
    successMetricLabel: "Orders placed",
    script: [
        line("assistant", "Roma Pizzeria, good evening. What can I get for you?"),
        line("caller", "Hi, a large margherita and a Diavola please."),
        line("assistant", "A large Margherita and a Diavola. Would you like the Diavola large as well?"),
        line("caller", "Medium is fine."),
        line("assistant", "Large Margherita, medium Diavola. Is this for delivery or collection?"),
        line("caller", "Delivery, 14 Via Roma."),
        line("assistant", "Delivery to 14 Via Roma. That comes to €27.40, about 35 minutes. Shall I place it?"),
        line("caller", "Yes please."),
        line("assistant", "Done. Order 2231 is with the kitchen. Thank you, goodnight."),
    ],
    referenceSchema: "pizzabot-menu-md-v1",
    answeringHours: ASKED_IN_OPENING_HOURS,
};
const surveyAgentTemplate = {
    ...NEVER_PASSING,
    mayEndConversation: false,
    secondModelBackupEligible: true,
    protectedCapability: [],
    conversationEngine: { id: "survey", supportedMedia: ["phone", "web_chat", "sandbox"] },
    agentTemplateId: id(AGENT_TEMPLATE_UUID.survey),
    code: "survey",
    direction: "outbound",
    customerOffered: true,
    name: "Survey",
    dek: "Calls a list of people, asks your questions in their language and saves their answers for you.",
    youAdd: "your questions",
    icon: "ph-duotone ph-clipboard-text",
    available: true,
    contentTitle: "Your questions, and who is asking",
    contentDek: "Upload your questions. We write every line the agent will say, for you to check before the " +
        "first call. Who to call is chosen later, in each campaign. Answers are filed here; there is " +
        "nothing to connect.",
    fieldSet: SURVEY_FIELD_SET,
    defaultGreeting: "Hello, this is the Autumn customer survey for Roma Pizzeria. Do you have two minutes?",
    defaultName: "Autumn survey",
    outcomeVocabulary: ["Completed", "Partial", "Declined", "No answer"],
    successMetricLabel: "Surveys completed",
    script: [
        line("assistant", "Hello, this is the Autumn customer survey for Roma Pizzeria. Do you have two minutes?"),
        line("caller", "Sure."),
        line("assistant", "Thank you. First: on a scale of one to five, how was your last order?"),
        line("caller", "A four."),
        line("assistant", "Four. Was the delivery on time?"),
        line("caller", "Yes, a little early actually."),
        line("assistant", "Noted. Last one: anything we should change?"),
        line("caller", "More vegetarian options."),
        line("assistant", "More vegetarian options. That is everything, thank you for your time."),
    ],
    referenceSchema: "surveybot-survey-json-v1",
    // A survey mostly calls out, and when it may call is each campaign's calling window. It has no
    // opening hours to ask for, and none is invented for it: it answers at every hour.
    answeringHours: { kind: "always" },
};
/**
 * The three that appear and are not buildable — booking here, leads and receptionist after support, which was the
 * fourth until plan-018. Copy verbatim from the prototype.
 */
const bookingAgentTemplate = {
    ...EXTERNAL_PASSING,
    mayEndConversation: true,
    secondModelBackupEligible: true,
    protectedCapability: [],
    conversationEngine: null,
    agentTemplateId: id(AGENT_TEMPLATE_UUID.booking),
    code: "booking",
    direction: "inbound",
    customerOffered: true,
    name: "Appointment booking",
    dek: "Books, moves and cancels appointments against your calendar and sends a confirmation.",
    youAdd: "calendar · services",
    icon: "ph-duotone ph-calendar-check",
    available: false,
    contentTitle: "Your calendar and services",
    contentDek: "The agent offers free slots from the connected calendar and books directly into it.",
    fieldSet: [],
    defaultGreeting: "Thanks for calling. Would you like to book, move or cancel an appointment?",
    defaultName: "Front desk bookings",
    outcomeVocabulary: ["Booked", "Rescheduled", "Cancelled", "Transferred"],
    successMetricLabel: "Appointments booked",
    script: [],
    referenceSchema: "none",
    answeringHours: ASKED_IN_OPENING_HOURS,
};
/**
 * CUSTOMER SUPPORT — the template that answers from the customer's own information (plan-018 CAT, design §30.3.5,
 * ADR 0058; owner answer Q1 of 2026-10-13, "Replace 'Customer support'"). The coming-soon row keeps its id, code,
 * name, icon and place in the picker and becomes available. What the owner gives is pasted text, documents and a
 * website address — any one of the three, or all: they share the required group `information`, which the wizard and
 * the api enforce from the field set (`missingRequiredGroupOf`, ED-31), so no code branches on this code. Pasted
 * text is the existing `allowText` upload (stored as a plain-text AgentDocument, pinned by SHA-256 — design §30.0.3
 * D8), documents take up to ten files, and the website address is the `website` kind the factory reads at build. Its
 * answering hours are asked as delivery asks them, in its own `opening_hours` field; hours found in the information
 * are facts the VoiceAgent can quote, never its answering hours.
 *
 * Its extraction is an information extraction, `wetalk-information-index-v1`, written by the factory's index builder
 * — not one of the frontier-model grammars of `REFERENCE_SCHEMA_CODE`. Its engine is the shared `model` engine with
 * three capabilities, from which the runtime derives the retrieval step, the fixed tool order and the HANDOFF line
 * (D6). Migration 0148 publishes the same values from `tools/catalogue-emit/input/agent-template/2026-10-13.json`,
 * applied after every app is deployed (ED-28), and `agent-template-catalogue.test.ts` pins the three together.
 */
const SUPPORT_FIELD_SET = [
    {
        kind: "upload",
        code: "information_text",
        label: "Your information — paste it or attach a text file",
        accept: [".txt"],
        allowText: true,
        requiredGroup: "information",
    },
    {
        kind: "upload",
        code: "information_document",
        label: "Documents",
        accept: [".txt", ".md", ".csv", ".pdf", ".png", ".jpg", ".jpeg"],
        maxFileCount: INFORMATION_DOCUMENT_FILE_LIMIT,
        requiredGroup: "information",
    },
    { kind: "website", code: "website_address", label: "Your website address", requiredGroup: "information" },
    { kind: "text", code: "opening_hours", label: "Opening hours", maxLength: 240 },
];
const supportAgentTemplate = {
    ...EXTERNAL_PASSING,
    mayEndConversation: true,
    secondModelBackupEligible: false,
    protectedCapability: [],
    conversationEngine: {
        id: "model",
        supportedMedia: ["phone", "web_chat", "sandbox"],
        capability: ["information_retrieval", "message_taking", "person_handoff"],
    },
    agentTemplateId: id(AGENT_TEMPLATE_UUID.support),
    code: "support",
    direction: "inbound",
    customerOffered: true,
    name: "Customer support",
    dek: "Answers questions from your own text, files and website, takes a message when it cannot, and can put callers " +
        "through to a person.",
    youAdd: "your text · documents · your website address",
    icon: "ph-duotone ph-lifebuoy",
    available: true,
    contentTitle: "What the agent should know",
    contentDek: "Paste your information, attach documents, or give your website address — any of the three, or all. The agent " +
        "answers only from them and takes a message when it cannot.",
    fieldSet: SUPPORT_FIELD_SET,
    defaultGreeting: "Hello, how can I help you?",
    defaultName: "Customer support",
    outcomeVocabulary: ["Answered", "Message taken", "Could not answer", "Handed to a person", "Hung up"],
    successMetricLabel: "Questions answered",
    script: [
        line("caller", "Hi, are you open on Saturday?"),
        line("assistant", "Yes, on Saturdays we are open from 10:00 to 14:00."),
        line("caller", "Do you deliver to Patras?"),
        line("assistant", "I don't have that information, but I can take a message and someone will get back to you. Would you like that?"),
    ],
    referenceSchema: INFORMATION_REFERENCE_SCHEMA,
    answeringHours: ASKED_IN_OPENING_HOURS,
};
const leadsAgentTemplate = {
    ...EXTERNAL_PASSING,
    mayEndConversation: true,
    secondModelBackupEligible: true,
    protectedCapability: [],
    conversationEngine: null,
    agentTemplateId: id(AGENT_TEMPLATE_UUID.leads),
    code: "leads",
    direction: "inbound",
    customerOffered: true,
    name: "Lead qualification",
    dek: "Answers new enquiries, asks your qualifying questions and books the follow-up.",
    youAdd: "questions · CRM webhook",
    icon: "ph-duotone ph-funnel",
    available: false,
    contentTitle: "Your qualifying questions",
    contentDek: "Mark which answers disqualify. Qualified leads are posted to your CRM with the transcript.",
    fieldSet: [],
    defaultGreeting: "Thanks for your interest. Can I ask a few questions to find the right person for you?",
    defaultName: "Inbound leads",
    outcomeVocabulary: ["Qualified", "Not a fit", "Follow-up booked", "Hung up"],
    successMetricLabel: "Leads qualified",
    script: [],
    referenceSchema: "none",
    answeringHours: ASKED_IN_OPENING_HOURS,
};
const receptionistAgentTemplate = {
    ...EXTERNAL_PASSING,
    mayEndConversation: true,
    secondModelBackupEligible: true,
    protectedCapability: [],
    conversationEngine: null,
    agentTemplateId: id(AGENT_TEMPLATE_UUID.receptionist),
    code: "receptionist",
    direction: "inbound",
    customerOffered: true,
    name: "Receptionist",
    dek: "Greets callers, answers the basics and transfers to the right person.",
    youAdd: "staff directory · transfer rules",
    icon: "ph-duotone ph-headset",
    available: false,
    contentTitle: "Who works here and who gets what",
    contentDek: "The directory tells the agent who to transfer to. Rules cover the rest: billing, urgent, " +
        "after hours.",
    fieldSet: [],
    defaultGreeting: "Roma Pizzeria, how can I direct your call?",
    defaultName: "Reception",
    outcomeVocabulary: ["Transferred", "Answered directly", "Message taken", "Hung up"],
    successMetricLabel: "Reached the right person",
    script: [],
    referenceSchema: "none",
    answeringHours: ASKED_IN_OPENING_HOURS,
};
/**
 * THE HIDDEN `help` TEMPLATE (plan-019 TPL, OD-20, design §31.3.4, ADR 0065; IC-H15). The wetalk help assistant runs as a
 * VoiceAgent of the platform-owned account built from this template — never offered to a customer: it is not in
 * {@link AGENT_TEMPLATE_CATALOGUE} (the picker's list), its row says `customer_offered = false` (0155), `GET
 * /v1/templates` filters on that column and `POST /v1/agents` refuses its code exactly as it refuses an unknown one. It
 * is available, because the factory builds only an available template: the worker writes the draft `{agent_template_code:
 * "help", field_value: {documentation: "<the eight file names>"}, translated_companion: "none"}` with the eight bundle
 * documents (design §31.11.3) and the factory runs the information build as for `support`.
 *
 * It answers at every hour (`always`: it has no opening hours, and `information_retrieval` admits outside them anyway),
 * in a web chat only (the help socket), with the five capabilities of the fixed help tool set (ED-11). Its one field is
 * the documentation upload: Markdown, up to the bundle's eight files, no pasted text. The copy below is never shown to a
 * customer; the name is the brand's, lowercase ("wetalk help"; in Greek «Βοήθεια wetalk», which no catalog needs while
 * it is never offered). Migration 0156 publishes the same row from
 * `tools/catalogue-emit/input/agent-template/2026-10-15-help.json`, and `agent-template-catalogue.test.ts` pins the two.
 */
const HELP_FIELD_SET = [
    {
        kind: "upload",
        code: "documentation",
        label: "wetalk documentation",
        accept: [".md"],
        maxFileCount: HELP_DOCUMENT_FILE_NAME.length,
    },
];
const helpAgentTemplate = {
    ...NEVER_PASSING,
    mayEndConversation: false,
    secondModelBackupEligible: false,
    protectedCapability: [],
    conversationEngine: { id: "model", supportedMedia: ["web_chat"], capability: HELP_ENGINE_CAPABILITY },
    agentTemplateId: id(AGENT_TEMPLATE_UUID.help),
    code: HELP_AGENT_TEMPLATE_CODE,
    direction: "inbound",
    customerOffered: false,
    name: "wetalk help",
    dek: "Answers questions about wetalk from its own documentation, and passes anything else to the wetalk team.",
    youAdd: "the wetalk documentation",
    icon: "ph-duotone ph-question",
    available: true,
    contentTitle: "The wetalk documentation",
    contentDek: "The eight documentation files of one documentation build, in English and Greek.",
    fieldSet: HELP_FIELD_SET,
    defaultGreeting: "Hi, I'm the wetalk assistant. What would you like to know?",
    defaultName: "wetalk help",
    outcomeVocabulary: ["Answered", "Request offered", "Could not answer", "Person joined"],
    successMetricLabel: "Questions answered",
    script: [
        line("caller", "Can you help me with my account?"),
        line("assistant", "Yes. Tell me what you are trying to do, and I'll answer from the wetalk documentation."),
    ],
    referenceSchema: INFORMATION_REFERENCE_SCHEMA,
    answeringHours: { kind: "always" },
};
const biAgentTemplate = {
    ...INTERNAL_PASSING,
    mayEndConversation: true,
    secondModelBackupEligible: true,
    protectedCapability: ["bi_query"],
    agentTemplateId: id(AGENT_TEMPLATE_UUID.bi), code: "bi", name: "BI Agent", direction: "inbound", customerOffered: true,
    dek: "Answers questions from live databases and connected sources, with calculations and source references.",
    youAdd: "databases · warehouses · CSV · JSON", icon: "ph-duotone ph-graph", available: true,
    contentTitle: "Connect your data",
    contentDek: "Connect PostgreSQL, MySQL, Microsoft SQL Server, MongoDB, Snowflake, ClickHouse or Elasticsearch, add public HTTPS JSON sources or upload CSV/JSON files. Build maps the selected schema and relationships without copying your database. Connected sources are queried for each question; uploaded files stay fixed until replaced. Use read-only access and choose data intended for this agent's audience.",
    fieldSet: [
        { kind: "upload", code: "bi_documents", label: "Datasets", accept: [".csv", ".json"], maxFileCount: 10, requiredGroup: "bi_data" },
        { kind: "text", code: "bi_configuration", label: "Data connections and relationships", maxLength: 262144, requiredGroup: "bi_data" },
    ],
    defaultGreeting: "Hello, what would you like to know about your data?", defaultName: "BI Agent",
    outcomeVocabulary: ["Answered", "Could not answer", "Hung up"], successMetricLabel: "Questions answered",
    script: [line("caller", "How many orders are in my data?"), line("assistant", "I'll query your current data, count the orders and show the source.")],
    referenceSchema: "wetalk-bi-live-v2", answeringHours: { kind: "always" },
    conversationEngine: { id: "model", supportedMedia: ["phone", "web_chat", "sandbox"] },
};
/**
 * The customer catalogue, in picker order: the two first templates, then the four that were on the way — `support`
 * keeps its place among them now that it is available (owner answer Q1, 2026-10-13) — then the BI Agent (`bi`). Every
 * row is offered to customers (plan-019 TPL): the console's picker renders exactly these, so the hidden `help` template
 * is not among them — it is in {@link EVERY_AGENT_TEMPLATE}.
 */
export const AGENT_TEMPLATE_CATALOGUE = [
    deliveryAgentTemplate,
    surveyAgentTemplate,
    bookingAgentTemplate,
    supportAgentTemplate,
    leadsAgentTemplate,
    receptionistAgentTemplate,
    biAgentTemplate,
];
/** The hidden help template's row (plan-019 TPL): what the help host, the worker and the factory read by code. */
export const HELP_AGENT_TEMPLATE = helpAgentTemplate;
/**
 * Every template of the compiled catalogue: the customer catalogue, then the templates never offered to customers
 * (`help`). What the build reads by code ({@link agentTemplateOf}, {@link answeringHoursOf}); a customer surface reads
 * {@link AGENT_TEMPLATE_CATALOGUE}.
 */
export const EVERY_AGENT_TEMPLATE = [...AGENT_TEMPLATE_CATALOGUE, helpAgentTemplate];
/** One row by its code — the hidden `help` template included — or `null`. A missing template is never substituted for another. */
export function agentTemplateOf(code) {
    const found = EVERY_AGENT_TEMPLATE.find((one) => one.code === code);
    return found === undefined ? null : found;
}
/**
 * Whether the second model backup may serve a conversation of the template with this code (plan-027 §9.5, 2026-10-24;
 * `CataloguedAgentTemplate.secondModelBackupEligible`). A code the compiled catalogue does not hold is NOT eligible: an
 * unknown template is never served where a known one would not be.
 */
export function secondModelBackupEligibleOf(code) {
    const found = agentTemplateOf(code);
    return found !== null && found.secondModelBackupEligible;
}
/**
 * How the template with this code sets its answering hours, or `null` when the compiled catalogue
 * holds no such template — which the build refuses rather than reading as either kind.
 */
export function answeringHoursOf(code) {
    const found = agentTemplateOf(code);
    return found === null ? null : found.answeringHours;
}
/** Only these can be built — the hidden `help` template among them. The other three render and refuse. */
export function buildableAgentTemplate() {
    return EVERY_AGENT_TEMPLATE.filter((one) => one.available);
}
//# sourceMappingURL=agent-template-catalogue.js.map